import ApiService from './ApiService';
import { PAGINATION_CONSTANTS } from '../constants/pagination';

export interface Requirement {
  id: string | number;
  work_order_ref_id?: string;
  client_id?: number;
  crdId: string;
  workOrderId: string;
  clientName: string;
  startDate: string;
  completionDate: string;
  requirementTitle: string;
  imagingModalities: string[];
  signedOffBy: string;
  signOffDate: string;
  status: 'Approved' | 'Pending' | 'Rejected' | 'INITIATED' | 'APPROVED' | 'PENDING' | 'REJECTED';
  work_order_status?: string; // Separate field for work order status (INITIATED, IN PROGRESS, etc.)
  mouId?: string;
  studyId?: string;
  studyTitle?: string;
  fileFormat?: string;
  numberOfFiles?: number;
  annotationRequirements?: string;
  reportingFormat?: string;
  documentUpload?: string;
  created_at?: string;
  updated_at?: string;
  // New fields from API
  end_date?: string | null;
  is_active?: boolean;
  client?: {
    id: number;
    name: string;
    email: string;
    phone: string;
    username: string;
    is_active: boolean;
  };
  requirement_doc?: {
    id: number;
    crd_id: string;
    work_order_id: number;
    title: string;
    annotation_requirements: string;
    modality: string[];
    file_format: string;
    reporting_format: string;
    document_id: string | null;
    no_of_files: number;
    status: string;
    signed_by: string;
    signed_date: string;
    is_active: boolean;
    created_by: any;
    created_at: string;
    updated_by: any;
    updated_at: string;
  };
  file_data?: any;
}

export interface WorkOrder {
  workOrderId: string;
  clientName: string;
  mouId: string;
  studyId: string;
  studyTitle: string;
  startDate: string;
  expectedCompletionDate: string;
  status?: 'Initiated' | 'In Progress' | 'Completed' | 'Cancelled';
}

export interface ClientRequirementDocument {
  crdId: string;
  requirementTitle: string;
  imagingModalities: string[];
  fileFormat: string | string[]; // Support both single string (old) and array (new multiselect)
  numberOfFiles?: number;
  annotationRequirements: string;
  reportingFormat: string;
  signedOffBy: string;
  signOffDate: string;
  status?: 'Approved' | 'Pending' | 'Rejected';
  documentUpload?: string;
}

export interface CreateRequirementRequest {
  workOrder: WorkOrder;
  crd: ClientRequirementDocument;
}

export interface FileData {
  url: string;
  original_file_name: string;
  s3_file_name: string;
  bucket_name: string;
  file_type: string;
  file_size: string;
  path: string;
  content_type: string;
}

export interface WorkOrderApiRequest {
  work_order_id?: number; // Optional: only sent when updating
  client_id: number;
  work_order_ref_id?: string;
  mou_id: string;
  study_id: string;
  study_title: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  requirement_doc: {
    crd_id: string;
    title: string;
    annotation_requirements: string;
    modality: string[];
    file_format: string;
    reporting_format: string;
    document_id: string | null;
    no_of_files: number;
    status: string;
    signed_by: string;
    signed_date: string;
    is_active: boolean;
  };
  file_data: FileData;
}

export interface WorkOrderApiResponse {
  is_success: boolean;
  status: number;
  message: string;
  data?: any;
}

export interface UpdateRequirementRequest {
  id: string;
  workOrder: Partial<WorkOrder>;
  crd: Partial<ClientRequirementDocument>;
}

export interface RequirementListResponse {
  requirements: Requirement[];
  total_count: number;
  page: number;
  size: number;
  total_pages: number;
}

export interface GetRequirementsRequest {
  search?: string | null; // Combined search for CRD ID / Work Order ID (sent as null when not applied)
  client_id?: string | null; // Client ID filter (sent as 'all' when not applied)
  start_date?: string | null; // Filter by start date (sent as null when not applied)
  end_date?: string | null; // Filter by end date/completion date (sent as null when not applied)
  status?: string | null; // Status filter (sent as 'all' when not applied)
  signed_date?: string | null; // Filter by signed date (sent as null when not applied)
  page?: number;
  size?: number;
  is_active?: boolean | 'all';
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

class RequirementService {
  private static instance: RequirementService;
  private apiService: ApiService;

  private constructor() {
    this.apiService = ApiService.getInstance();
  }

  public static getInstance(): RequirementService {
    if (!RequirementService.instance) {
      RequirementService.instance = new RequirementService();
    }
    return RequirementService.instance;
  }

  public async getRequirements(params: GetRequirementsRequest = {}): Promise<ApiResponse<RequirementListResponse>> {
    try {
      // Build query params object - send all keys even if they are null
      const queryParams: any = {
        search: params.search ?? null,
        client_id: params.client_id || 'all',
        start_date: params.start_date ?? null,
        end_date: params.end_date ?? null,
        status: params.status || 'all',
        signed_date: params.signed_date ?? null,
        page: params.page || 1,
        size: params.size || PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE
      };

      const response = await this.apiService.get<{
        is_success: boolean;
        status: number;
        message: string;
        data: [{
          work_orders: any[];
          total?: number;
          total_count?: number;
          page: number;
          size?: number;
          page_size?: number;
          total_pages?: number;
        }];
      }>('/work-orders', queryParams, this.apiService.getWorkOrderServiceUrl());

      if (response.success && response.data && response.data.is_success) {
        const workOrderData = response.data.data[0];

        // Map API response to UI format
        const requirements: Requirement[] = workOrderData.work_orders.map(workOrder => ({
          id: workOrder.id,
          work_order_ref_id: workOrder.work_order_ref_id,
          client_id: workOrder.client_id,
          crdId: workOrder.requirement_doc?.crd_id || '',
          workOrderId: workOrder.work_order_ref_id || workOrder.id.toString(),
          clientName: workOrder.client?.name || '',
          startDate: workOrder.start_date || '',
          completionDate: workOrder.end_date || '',
          requirementTitle: workOrder.requirement_doc?.title || '',
          imagingModalities: workOrder.requirement_doc?.modality || [],
          signedOffBy: workOrder.requirement_doc?.signed_by || '',
          signOffDate: workOrder.requirement_doc?.signed_date || '',
          status: workOrder.requirement_doc?.status || 'APPROVED', // CRD status (for table display and backward compatibility)
          work_order_status: workOrder.status || 'INITIATED', // Work Order status (INITIATED, IN PROGRESS, etc.)
          mouId: workOrder.mou_id,
          studyId: workOrder.study_id,
          studyTitle: workOrder.study_title,
          fileFormat: workOrder.requirement_doc?.file_format,
          numberOfFiles: workOrder.requirement_doc?.no_of_files,
          annotationRequirements: workOrder.requirement_doc?.annotation_requirements,
          reportingFormat: workOrder.requirement_doc?.reporting_format,
          documentUpload: workOrder.requirement_doc?.file?.original_file_name || '',
          created_at: workOrder.created_at,
          updated_at: workOrder.updated_at,
          end_date: workOrder.end_date,
          is_active: workOrder.is_active,
          client: workOrder.client,
          requirement_doc: workOrder.requirement_doc,
          file_data: workOrder.requirement_doc?.file
        }));

        const totalCount = workOrderData.total_count ?? workOrderData.total ?? requirements.length;
        const pageNumber = workOrderData.page ?? params.page ?? 1;
        const pageSize = workOrderData.page_size ?? workOrderData.size ?? params.size ?? PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE;
        const totalPages = workOrderData.total_pages ?? Math.ceil(totalCount / pageSize);

        const requirementListResponse: RequirementListResponse = {
          requirements: requirements,
          total_count: totalCount,
          page: pageNumber,
          size: pageSize,
          total_pages: totalPages
        };

        return {
          success: true,
          data: requirementListResponse
        };
      } else {
        return {
          success: false,
          error: response.data?.message || response.error || 'Failed to fetch requirements'
        };
      }
    } catch (error) {
      console.error('Error fetching requirements:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  public async createRequirement(requirementData: CreateRequirementRequest, fileData?: FileData): Promise<ApiResponse<Requirement>> {
    try {
      // Helper function to convert date to ISO format with UTC timezone
      const formatDateToISO = (dateString: string): string => {
        if (!dateString) return '';
        // Create date at midnight UTC
        const date = new Date(dateString + 'T00:00:00Z');
        return date.toISOString().replace(/\.\d{3}Z$/, 'Z'); // Format: "2025-09-20T00:00:00Z"
      };

      // Transform the request data to match the API format
      const apiRequest: WorkOrderApiRequest = {
        client_id: parseInt(requirementData.workOrder.clientName) || 1, // clientName now contains the client_id
        work_order_ref_id: requirementData.workOrder.workOrderId,
        mou_id: requirementData.workOrder.mouId,
        study_id: requirementData.workOrder.studyId,
        study_title: requirementData.workOrder.studyTitle,
        start_date: formatDateToISO(requirementData.workOrder.startDate),
        end_date: formatDateToISO(requirementData.workOrder.expectedCompletionDate),
        is_active: true,
        requirement_doc: {
          crd_id: requirementData.crd.crdId,
          title: requirementData.crd.requirementTitle,
          annotation_requirements: requirementData.crd.annotationRequirements,
          modality: requirementData.crd.imagingModalities,
          file_format: requirementData.crd.fileFormat,
          reporting_format: requirementData.crd.reportingFormat,
          document_id: null,
          no_of_files: requirementData.crd.numberOfFiles || 0,
          status: (requirementData.crd.status || 'APPROVED').toUpperCase(),
          signed_by: requirementData.crd.signedOffBy,
          signed_date: formatDateToISO(requirementData.crd.signOffDate),
          is_active: true
        },
        file_data: fileData || {
          url: '',
          original_file_name: requirementData.crd.documentUpload || '',
          s3_file_name: '',
          bucket_name: '',
          file_type: '',
          file_size: '0',
          path: '',
          content_type: 'application/pdf'
        }
      };

      const response = await this.apiService.post<WorkOrderApiResponse>(
        '/work-orders',
        apiRequest,
        this.apiService.getWorkOrderServiceUrl()
      );

      if (response.success && response.data && response.data.is_success) {
        // Map the response back to our Requirement format
        const newRequirement: Requirement = {
          id: response.data.data?.id || Date.now().toString(),
          crdId: requirementData.crd.crdId,
          workOrderId: requirementData.workOrder.workOrderId,
          clientName: requirementData.workOrder.clientName,
          startDate: requirementData.workOrder.startDate,
          completionDate: requirementData.workOrder.expectedCompletionDate,
          requirementTitle: requirementData.crd.requirementTitle,
          imagingModalities: requirementData.crd.imagingModalities,
          signedOffBy: requirementData.crd.signedOffBy,
          signOffDate: requirementData.crd.signOffDate,
          status: requirementData.crd.status,
          mouId: requirementData.workOrder.mouId,
          studyId: requirementData.workOrder.studyId,
          studyTitle: requirementData.workOrder.studyTitle,
          fileFormat: requirementData.crd.fileFormat,
          numberOfFiles: requirementData.crd.numberOfFiles,
          annotationRequirements: requirementData.crd.annotationRequirements,
          reportingFormat: requirementData.crd.reportingFormat,
          documentUpload: requirementData.crd.documentUpload,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };

        return {
          success: true,
          data: newRequirement,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data?.message || response.error || 'Failed to create requirement'
        };
      }
    } catch (error) {
      console.error('Error creating requirement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  public async updateRequirement(id: string, requirementData: UpdateRequirementRequest, fileData?: FileData): Promise<ApiResponse<Requirement>> {
    try {
      // Helper function to convert date to ISO format with UTC timezone
      const formatDateToISO = (dateString: string): string => {
        if (!dateString) return '';
        // Handle both YYYY-MM-DD and ISO formats
        if (dateString.includes('T')) {
          return dateString; // Already in ISO format
        }
        // Create date at midnight UTC
        const date = new Date(dateString + 'T00:00:00Z');
        return date.toISOString().replace(/\.\d{3}Z$/, 'Z'); // Format: "2025-09-20T00:00:00Z"
      };

      // Transform the request data to match the API format
      const apiRequest: WorkOrderApiRequest = {
        work_order_id: parseInt(id), // Include work_order_id for update
        client_id: parseInt(requirementData.workOrder.clientName) || 1, // clientName now contains the client_id
        work_order_ref_id: requirementData.workOrder.workOrderId,
        mou_id: requirementData.workOrder.mouId || '',
        study_id: requirementData.workOrder.studyId || '',
        study_title: requirementData.workOrder.studyTitle || '',
        start_date: formatDateToISO(requirementData.workOrder.startDate || ''),
        end_date: formatDateToISO(requirementData.workOrder.expectedCompletionDate || ''),
        is_active: true,
        requirement_doc: {
          crd_id: requirementData.crd.crdId || '',
          title: requirementData.crd.requirementTitle || '',
          annotation_requirements: requirementData.crd.annotationRequirements || '',
          modality: requirementData.crd.imagingModalities || [],
          file_format: requirementData.crd.fileFormat || '',
          reporting_format: requirementData.crd.reportingFormat || '',
          document_id: null,
          no_of_files: requirementData.crd.numberOfFiles || 0,
          status: (requirementData.crd.status || 'APPROVED').toUpperCase(),
          signed_by: requirementData.crd.signedOffBy || '',
          signed_date: formatDateToISO(requirementData.crd.signOffDate || ''),
          is_active: true
        },
        file_data: fileData || {
          url: '',
          original_file_name: requirementData.crd.documentUpload || '',
          s3_file_name: '',
          bucket_name: '',
          file_type: '',
          file_size: '0',
          path: '',
          content_type: 'application/pdf'
        }
      };

      const response = await this.apiService.post<WorkOrderApiResponse>(
        '/work-orders',
        apiRequest,
        this.apiService.getWorkOrderServiceUrl()
      );

      if (response.success && response.data && response.data.is_success) {
        // Map the response back to our Requirement format
        const updatedRequirement: Requirement = {
          id: id,
          crdId: requirementData.crd.crdId || '',
          workOrderId: requirementData.workOrder.workOrderId || '',
          clientName: requirementData.workOrder.clientName || '',
          startDate: requirementData.workOrder.startDate || '',
          completionDate: requirementData.workOrder.expectedCompletionDate || '',
          requirementTitle: requirementData.crd.requirementTitle || '',
          imagingModalities: requirementData.crd.imagingModalities || [],
          signedOffBy: requirementData.crd.signedOffBy || '',
          signOffDate: requirementData.crd.signOffDate || '',
          status: requirementData.crd.status || 'Approved',
          work_order_status: requirementData.workOrder.status,
          mouId: requirementData.workOrder.mouId,
          studyId: requirementData.workOrder.studyId,
          studyTitle: requirementData.workOrder.studyTitle,
          fileFormat: requirementData.crd.fileFormat,
          numberOfFiles: requirementData.crd.numberOfFiles,
          annotationRequirements: requirementData.crd.annotationRequirements,
          reportingFormat: requirementData.crd.reportingFormat,
          documentUpload: requirementData.crd.documentUpload,
          updated_at: new Date().toISOString()
        };

        return {
          success: true,
          data: updatedRequirement,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          error: response.data?.message || response.error || 'Failed to update requirement'
        };
      }
    } catch (error) {
      console.error('Error updating requirement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  public async deleteRequirement(id: string | number): Promise<ApiResponse<void>> {
    try {
      const response = await this.apiService.delete<{
        is_success: boolean;
        status: number;
        message: string;
        data: [{
          work_order_id: number;
        }];
      }>(`/work-orders/work_order_id=${id}`, undefined, this.apiService.getWorkOrderServiceUrl());

      if (response.success && response.data && response.data.is_success) {
        return {
          success: true,
          message: response.data.message
        };
      }

      return {
        success: false,
        error: response.data?.message || response.error || 'Failed to delete requirement'
      };
    } catch (error) {
      console.error('Error deleting requirement:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred while deleting requirement'
      };
    }
  }

  public async getRequirementById(id: string): Promise<ApiResponse<Requirement>> {
    // Mock implementation - replace with actual API call
    const mockRequirement: Requirement = {
      id: id,
      crdId: 'CRD-20250920-001',
      workOrderId: 'WO20250915-001',
      clientName: 'XYZ Imaging Center',
      startDate: '20 Sep 2025',
      completionDate: '25 Nov 2025',
      requirementTitle: 'OCT Grading for DR Patients',
      imagingModalities: ['OCT'],
      signedOffBy: 'Dr. A. Kumar',
      signOffDate: '21 Sep 2025',
      status: 'Approved',
      mouId: 'MOU-20250901',
      studyId: 'DR-STUDY-001',
      studyTitle: 'Diabetic Retinopathy OCT Grading',
      fileFormat: 'DICOM',
      numberOfFiles: 500,
      annotationRequirements: 'Central Macular Thickness, Presence of Fluid, DR Severity',
      reportingFormat: 'Excel',
      documentUpload: 'Document.pdf',
      created_at: '2025-09-20T10:00:00Z',
      updated_at: '2025-09-21T14:30:00Z'
    };

    return {
      success: true,
      data: mockRequirement
    };
  }
}

export default RequirementService;
